import { FC, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button, Container } from '@mui/material';

import Breadcrumbs from '@components/Breadcrumbs/Breadcrumbs';
import Loadable from '@components/Loadable/Loadable';
import MarkdownDisplay from '@components/MarkdownDisplay/markdown';
import {
  fetchResourceDetails,
  selectResourceDetails,
  selectResourceError,
  selectResourceIsLoading,
} from '@store/features/recources/slice';
import useDispatch from '@store/hooks/useDispatch';

import './style.scss';

const formatLink = (link: string) => {
  const absoluteRegex = /^(http:\/\/|https:\/\/)/;

  if (absoluteRegex.test(link)) {
    return link;
  }
  return `http://${link}`;
};

const ResourcePage: FC = () => {
  const { resourceId } = useParams<{
    resourceId: string;
    category: string;
  }>();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const categoryParams = queryParams.get('category');
  const category = useMemo(() => {
    if (categoryParams) {
      const decodedCategory = JSON.parse(decodeURIComponent(categoryParams));
      return {
        id: decodedCategory.id,
        title: decodedCategory.name,
      };
    }

    return null;
  }, [categoryParams]);

  const resourceDetails = useSelector(selectResourceDetails);
  const dispatch = useDispatch();
  const resourceIsLoading = useSelector(selectResourceIsLoading);
  const resourceError = useSelector(selectResourceError);

  useEffect(() => {
    if (resourceDetails && resourceDetails.id === parseInt(resourceId || '', 10)) {
      return;
    }
    dispatch(fetchResourceDetails(Number(resourceId)));
  }, [resourceId, resourceDetails]);

  const breadcrumbs = useMemo(() => {
    if (!category) {
      return null;
    }

    return [
      {
        link: `/category/${category.id}`,
        title: category.title,
      },
      {
        link: `/resource/${resourceId}`,
        title: resourceDetails?.title,
      },
    ];
  }, [category, resourceId, resourceDetails]);

  return (
    <Loadable isLoading={resourceIsLoading} error={resourceError || ''}>
      {resourceDetails ? (
        <Container maxWidth="md" className="resource-page">
          {breadcrumbs && <Breadcrumbs items={breadcrumbs} />}

          <Container maxWidth="md" className="resource-page__paper">
            {resourceDetails.title && (
              <div className="resource-page__title">{resourceDetails.title}</div>
            )}
            {resourceDetails.subtitle && (
              <div className="resource-page__subtitle">{resourceDetails.subtitle}</div>
            )}
            {resourceDetails.thumbnail && (
              <img className="resource-page__image" src={resourceDetails.thumbnail} />
            )}
            <MarkdownDisplay markdownText={resourceDetails.body} />
          </Container>
        </Container>
      ) : (
        <div className="resources-page__no-resource-container">
          <span className="resources-page__no-resource-text">No Resource here yet</span>
        </div>
      )}

      {resourceDetails?.link && (
        <div className="resource-page__link-container">
          <Link
            target="_blank"
            rel="noopener noreferrer"
            className="resource-page__resource-link-button"
            to={formatLink(resourceDetails.link)}
          >
            <Button variant="contained">Open link</Button>
          </Link>
        </div>
      )}
    </Loadable>
  );
};

export default ResourcePage;
